import { useCallback, useState } from "react";

import { DescriptionType } from "api/client/products";
import { useAnalytics } from "contexts/AnalyticsContext";
import { useSession } from "contexts/AuthContext";
import { FirestoreError } from "firebase/firestore";
import {
  IProduct,
  SubjectSchemeIdentifier,
  updateProduct,
  updateProductInTransaction,
} from "integrations/firebase/collections";
import {
  FirestoreProductBisacSubject,
  FirestoreProductSubject,
} from "reedy-data/models";
import { logError } from "shared/services/ErrorReporting";
import { useApi } from "./useApi";

export const useProductActions = () => {
  const { user } = useSession();

  const api = useApi();

  const [isLoadingGenerate, setIsLoadingGenerate] = useState(false);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [errorGenerate, setErrorGenerate] = useState<FirestoreError>();
  const [errorUpdate, setErrorUpdate] = useState<Error>();

  const { gaEvent } = useAnalytics();

  const generateKeywords = useCallback(
    async (products: IProduct[]) => {
      if (!products || products.length === 0) {
        return;
      }

      setIsLoadingGenerate(true);
      setErrorGenerate(undefined);

      try {
        await api.products.generateKeywordsBulk(
          products.map((product) => product.id),
        );
        products.forEach((product) => {
          gaEvent({
            type: "bulk_generate_keywords",
            payload: {
              book: product.id,
              item_list_id: product.id,
              item_list_name: product.title,
            },
          });
        });
      } catch (err) {
        logError(err);
        setErrorGenerate(err as FirestoreError);
      }

      setIsLoadingGenerate(false);
    },
    [gaEvent, api],
  );

  const generateDescriptions = useCallback(
    async (products: IProduct[], descriptionTypes: DescriptionType[]) => {
      if (!products?.length) {
        return;
      }

      setIsLoadingGenerate(true);
      setErrorGenerate(undefined);

      try {
        await api.products.generateDescriptionsBulk(
          products.map((product) => product.id),
          descriptionTypes,
        );
        products.forEach((product) => {
          gaEvent({
            type: "bulk_generate_descriptions",
            payload: {
              book: product.id,
              item_list_id: product.id,
              item_list_name: product.title,
            },
          });
        });
      } catch (err) {
        logError(err);
        setErrorGenerate(err as FirestoreError);
      }

      setIsLoadingGenerate(false);
    },
    [gaEvent, api],
  );

  const generateThemaCodes = useCallback(
    async (products: IProduct[]) => {
      if (!products?.length) {
        return;
      }

      setIsLoadingGenerate(true);
      setErrorGenerate(undefined);

      try {
        await api.products.generateThemaBulk(
          products.map((product) => product.id),
        );
        products.forEach((product) => {
          gaEvent({
            type: "bulk_generate_thema_codes",
            payload: {
              book: product.id,
              item_list_id: product.id,
              item_list_name: product.title,
            },
          });
        });
      } catch (err) {
        logError(err);
        setErrorGenerate(err as FirestoreError);
      }

      setIsLoadingGenerate(false);
    },
    [gaEvent, api],
  );

  const generateSubtitle = useCallback(
    async (product: IProduct, specialRequest: string | null) => {
      setIsLoadingGenerate(true);
      setErrorGenerate(undefined);

      try {
        await api.products.generateSubtitle(product.id, specialRequest);
        gaEvent({
          type: "generate_subtitle",
          payload: {
            book: product.id,
            special_request: specialRequest,
          },
        });
      } catch (err) {
        logError(err);
        setErrorGenerate(err as FirestoreError);
      }

      setIsLoadingGenerate(false);
    },
    [api, gaEvent],
  );

  const updateProductById = useCallback(
    async (
      productId: string,
      getUpdateObject: (product?: IProduct) => any,
      inTransaction = false,
    ) => {
      setIsLoadingUpdate(true);
      setErrorUpdate(undefined);

      try {
        if (inTransaction) {
          await updateProductInTransaction(productId, getUpdateObject);
        } else {
          await updateProduct(productId, getUpdateObject());
        }
      } catch (err) {
        logError(err);
        setErrorUpdate(err as Error);
        throw err;
      } finally {
        setIsLoadingUpdate(false);
      }
    },
    [],
  );

  const generateProductKeywords = useCallback(
    async (product: IProduct, options?: { regenerate?: boolean }) => {
      setIsLoadingUpdate(true);
      setErrorUpdate(undefined);

      try {
        const { id } = product;
        await api.products.generateKeywords(id);
        gaEvent({
          type: "generate_keywords",
          payload: {
            book: id,
            item_list_id: id,
            item_list_name: product?.title,
            regenerate: options?.regenerate,
          },
        });
        return true;
      } catch (err) {
        logError(err);
        setErrorUpdate(err as Error);
        return false;
      } finally {
        setIsLoadingUpdate(false);
      }
    },
    [gaEvent, api],
  );

  const generateProductDescription = useCallback(
    async (
      product: IProduct,
      hash: string,
      targetGroup: string | null,
      specialRequest: string | null,
      options?: { regenerate?: boolean },
    ) => {
      setIsLoadingGenerate(true);
      setErrorUpdate(undefined);

      try {
        await api.products.generateDescription(product.id, {
          hash,
          targetGroup,
          specialRequest,
        });
        gaEvent({
          type: "generate_description",
          payload: {
            book: product.id,
            item_list_id: product.id,
            item_list_name: product?.title,
            regenerate: options?.regenerate,
            target: targetGroup,
            details: specialRequest,
          },
        });
      } catch (err) {
        logError(err);
        setErrorUpdate(err as Error);
      }

      setIsLoadingGenerate(false);
    },
    [api, gaEvent],
  );

  const generateProductSubjects = useCallback(
    async (productId: string, options?: { regenerate?: boolean }) => {
      setIsLoadingGenerate(true);
      setErrorUpdate(undefined);

      try {
        await api.products.generateThema(productId);
        gaEvent({
          type: "generate_subjects",
          payload: {
            book: productId,
            regenerate: options?.regenerate,
          },
        });
      } catch (err) {
        logError(err);
        setErrorUpdate(err as Error);
      }

      setIsLoadingGenerate(false);
    },
    [api, gaEvent],
  );

  const saveFinalSubjects = useCallback(
    async (productId: string, subjects: FirestoreProductSubject[]) => {
      await updateProductById(productId, () => ({
        // The 500-character cap applies to generated keywords, not manual
        // final-list edits. Direct saves still normalize whitespace.
        "subjects.final": subjects
          .map((subject) => {
            if (
              subject.subjectSchemeIdentifier !==
              SubjectSchemeIdentifier.Keyword
            ) {
              return subject;
            }

            const keyword = subject.subjectHeadingText?.trim();
            if (!keyword) {
              return null;
            }

            return keyword === subject.subjectHeadingText
              ? subject
              : {
                  ...subject,
                  subjectHeadingText: keyword,
                };
          })
          .filter((subject) => subject !== null),
      }));
    },
    [updateProductById],
  );

  const saveFinalKeywords = useCallback(
    async (productId: string, subjects: FirestoreProductSubject[]) => {
      await updateProductById(productId, () => ({
        "subjects.final": subjects,
      }));
      await api.products.updateFinalKeywords(productId, subjects);
    },
    [updateProductById, api],
  );

  const saveFinalWordSubjects = useCallback(
    async (productId: string, subjects: FirestoreProductSubject[]) => {
      await updateProductById(productId, () => ({
        "subjects.finalWords": subjects.map((s) => ({
          ...s,
        })),
      }));
    },
    [updateProductById],
  );

  // Applies the change to the stored list, so an ONIX import in between is kept.
  const updateBisacSubjects = useCallback(
    async (
      productId: string,
      update: (
        subjects: FirestoreProductBisacSubject[],
      ) => FirestoreProductBisacSubject[],
    ) => {
      await updateProductById(
        productId,
        (product) => ({
          "subjects.bisac": update(product?.subjects?.bisac ?? []),
        }),
        true,
      );
    },
    [updateProductById],
  );

  const setProductDescription = useCallback(
    async (productId: string, descriptionHash: string, text: string) => {
      await updateProductById(
        productId,
        (product) => {
          if (!product) {
            throw new Error("Unable to find product to update");
          }

          if (!product.descriptions?.length) {
            throw new Error("Unable to find product description to update");
          }

          const descriptions = product.descriptions.map((description) => {
            if (description.hash === descriptionHash) {
              return {
                ...description,
                textGenerated: text,
              };
            }

            return description;
          });

          const generatedDescriptionDataPath = "generated.description.data";

          return {
            [`${generatedDescriptionDataPath}.text`]: text,
            [`${generatedDescriptionDataPath}.userId`]: user.id,
            [`${generatedDescriptionDataPath}.isManualInput`]: true,
            descriptions,
          };
        },
        true,
      );
    },
    [updateProductById, user.id],
  );

  return {
    isLoadingGenerate,
    errorGenerate,
    generateKeywords,
    generateDescriptions,
    generateThemaCodes,
    generateSubtitle,
    isLoadingUpdate,
    errorUpdate,
    generateProductKeywords,
    generateProductDescription,
    setProductDescription,
    generateProductSubjects,
    saveFinalSubjects,
    saveFinalKeywords,
    saveFinalWordSubjects,
    updateBisacSubjects,
  };
};
